import { Capacitor } from '@capacitor/core';
import { getOneSignalAdapter } from './index';

/**
 * Enhanced OneSignal SDK 5.x Debug Utilities
 * Specifically designed to diagnose and fix common SDK 5.x compatibility issues
 */

export interface OneSignalDiagnosticResult {
  success: boolean;
  message: string;
  details: {
    platform: string;
    sdkVersion: string;
    pluginAvailable: boolean;
    cordovaAvailable: boolean;
    methodsAvailable: any;
    connectionTest?: any;
    loginTest?: any;
    errorDetails?: any;
  };
  recommendations: string[];
}

/**
 * Comprehensive OneSignal SDK 5.x diagnostic
 */
export const runOneSignalSDK5Diagnostic = async (): Promise<OneSignalDiagnosticResult> => {
  const details: any = {
    platform: Capacitor.getPlatform(),
    sdkVersion: 'unknown',
    pluginAvailable: false,
    cordovaAvailable: !!window.cordova,
    methodsAvailable: {},
    recommendations: []
  };

  const recommendations: string[] = [];

  try {
    

    // Step 1: Basic plugin availability
    const adapter = getOneSignalAdapter();
    details.pluginAvailable = adapter.isAvailable();

    if (!details.pluginAvailable) {
      recommendations.push('OneSignal plugin is not available - check installation');
      return {
        success: false,
        message: 'OneSignal plugin not available',
        details,
        recommendations
      };
    }

    // Step 2: Detect SDK version and available methods
    const availableMethods = adapter.detectAvailableMethods();
    details.methodsAvailable = availableMethods;
    details.sdkVersion = availableMethods.sdkVersion || 'unknown';

    

    // Step 3: Test basic SDK connectivity
    try {
      const connectivityTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('SDK connectivity timeout')), 5000)
      );

      const connectivityTest = adapter.getDeviceState();
      const deviceState = await Promise.race([connectivityTest, connectivityTimeout]);
      
      details.connectionTest = {
        success: true,
        deviceState: deviceState ? {
          hasUserId: !!deviceState.userId,
          hasExternalId: !!deviceState.externalUserId,
          hasPermission: !!deviceState.hasNotificationPermission
        } : null
      };

      
    } catch (connectError) {
      details.connectionTest = {
        success: false,
        error: connectError instanceof Error ? connectError.message : 'Unknown error'
      };
      
      recommendations.push('SDK connectivity test failed - OneSignal may not be properly initialized');
      
    }

    // Step 4: Test SDK 5.x login methods specifically
    if (details.sdkVersion === '5.x') {
      
      
      const loginTestResults = await testSDK5LoginMethods(adapter);
      details.loginTest = loginTestResults;

      if (!loginTestResults.anyMethodWorked) {
        recommendations.push('All SDK 5.x login methods failed - this indicates a serious compatibility issue');
        recommendations.push('Consider downgrading to OneSignal SDK 4.x or check for plugin updates');
      }
    }

    // Step 5: Analyze common issues and provide recommendations
    analyzeAndRecommend(details, recommendations);

    // Step 6: Return diagnostic result
    const allTestsPassed = details.connectionTest?.success && (details.loginTest?.anyMethodWorked !== false);
    
    return {
      success: allTestsPassed,
      message: allTestsPassed 
        ? 'OneSignal SDK diagnostic completed successfully' 
        : 'OneSignal SDK has compatibility issues',
      details,
      recommendations
    };

  } catch (error) {
    details.errorDetails = {
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    };

    return {
      success: false,
      message: 'Diagnostic failed with error',
      details,
      recommendations: [...recommendations, 'Diagnostic failed - check console for detailed error information']
    };
  }
};

/**
 * Test SDK 5.x specific login methods
 */
const testSDK5LoginMethods = async (adapter: any): Promise<any> => {
  const testExternalId = 'diagnostic-test-' + Date.now();
  const results: any = {
    userLoginTest: { attempted: false, success: false, error: null },
    cordovaLoginTest: { attempted: false, success: false, error: null },
    cordovaSetExternalTest: { attempted: false, success: false, error: null },
    anyMethodWorked: false
  };

  // Test 1: User.login() method
  try {
    const plugin = adapter.getPlugin();
    if (plugin.User && typeof plugin.User.login === 'function') {
      results.userLoginTest.attempted = true;
      
      
      const userLoginPromise = plugin.User.login(testExternalId);
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('User.login() timeout')), 5000)
      );
      
      await Promise.race([userLoginPromise, timeoutPromise]);
      results.userLoginTest.success = true;
      results.anyMethodWorked = true;
      
      
    }
  } catch (error) {
    results.userLoginTest.error = error instanceof Error ? error.message : 'Unknown error';
    
  }

  // Test 2: Cordova login method
  if (window.cordova && window.cordova.exec) {
    try {
      results.cordovaLoginTest.attempted = true;
      
      
      const cordovaLoginPromise = new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Cordova login timeout'));
        }, 5000);
        
        window.cordova.exec(
          () => {
            clearTimeout(timeoutId);
            resolve();
          },
          (error: any) => {
            clearTimeout(timeoutId);
            reject(error);
          },
          'OneSignalPush',
          'login',
          [testExternalId]
        );
      });
      
      await cordovaLoginPromise;
      results.cordovaLoginTest.success = true;
      results.anyMethodWorked = true;
      
      
    } catch (error) {
      results.cordovaLoginTest.error = error instanceof Error ? error.message : 'Unknown error';
      
    }

    // Test 3: Cordova setExternalUserId method
    try {
      results.cordovaSetExternalTest.attempted = true;
      
      
      const cordovaSetExternalPromise = new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          reject(new Error('Cordova setExternalUserId timeout'));
        }, 5000);
        
        window.cordova.exec(
          () => {
            clearTimeout(timeoutId);
            resolve();
          },
          (error: any) => {
            clearTimeout(timeoutId);
            reject(error);
          },
          'OneSignalPush',
          'setExternalUserId',
          [testExternalId]
        );
      });
      
      await cordovaSetExternalPromise;
      results.cordovaSetExternalTest.success = true;
      results.anyMethodWorked = true;
      
      
    } catch (error) {
      results.cordovaSetExternalTest.error = error instanceof Error ? error.message : 'Unknown error';
      
    }
  }

  return results;
};

/**
 * Analyze diagnostic results and provide specific recommendations
 */
const analyzeAndRecommend = (details: any, recommendations: string[]) => {
  // Platform-specific recommendations
  if (details.platform === 'ios') {
    recommendations.push('iOS detected - ensure OneSignal iOS SDK is properly configured');
    
    if (!details.connectionTest?.success) {
      recommendations.push('iOS: Check that OneSignal app ID matches your iOS app configuration');
    }
  }

  // SDK version specific recommendations
  if (details.sdkVersion === '5.x') {
    recommendations.push('SDK 5.x detected - this version has different method signatures than previous versions');
    
    if (details.loginTest && !details.loginTest.anyMethodWorked) {
      recommendations.push('SDK 5.x login methods all failed - this suggests a plugin compatibility issue');
      recommendations.push('Try updating onesignal-cordova-plugin to the latest version');
      recommendations.push('Consider temporarily downgrading to SDK 4.x if issues persist');
    }
  }

  // Cordova-specific recommendations
  if (!details.cordovaAvailable) {
    recommendations.push('Cordova not available - ensure app is running on native platform');
  } else {
    if (details.loginTest?.cordovaLoginTest?.attempted && !details.loginTest.cordovaLoginTest.success) {
      recommendations.push('Cordova bridge communication failed - check plugin installation');
    }
  }

  // Method availability recommendations
  const methods = details.methodsAvailable;
  if (methods.sdkVersion === '5.x' && (!methods.userMethods || Object.keys(methods.userMethods).length === 0)) {
    recommendations.push('SDK 5.x detected but User methods not available - plugin may not be properly initialized');
  }
};

/**
 * Quick fix for common OneSignal SDK 5.x issues
 */
export const quickFixOneSignalSDK5Issues = async (): Promise<{
  success: boolean;
  message: string;
  actionsPerformed: string[];
}> => {
  const actionsPerformed: string[] = [];
  
  try {
    
    
    // Step 1: Wait for SDK to be ready
    actionsPerformed.push('Waited for SDK initialization');
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Step 2: Try to reinitialize with proper sequence
    const adapter = getOneSignalAdapter();
    if (adapter.isAvailable()) {
      actionsPerformed.push('Verified adapter availability');
      
      // Step 3: Try to get current state
      try {
        const deviceState = await adapter.getDeviceState();
        if (deviceState) {
          actionsPerformed.push('Successfully retrieved device state');
          
          // Step 4: If no external ID, try to set one for testing
          if (!deviceState.externalUserId) {
            try {
              await adapter.login('quick-fix-test-' + Date.now());
              actionsPerformed.push('Successfully tested login method');
            } catch (loginError) {
              actionsPerformed.push('Login method test failed - this indicates the core issue');
            }
          }
        }
      } catch (stateError) {
        actionsPerformed.push('Failed to retrieve device state - SDK not properly initialized');
      }
    } else {
      actionsPerformed.push('Adapter not available - plugin installation issue');
    }
    
    return {
      success: true,
      message: 'Quick fix completed - check actions performed for results',
      actionsPerformed
    };
    
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error during quick fix',
      actionsPerformed
    };
  }
};

/**
 * Export diagnostic functions for global use
 */
if (typeof window !== 'undefined') {
  (window as any).oneSignalSDK5Debug = {
    runDiagnostic: runOneSignalSDK5Diagnostic,
    quickFix: quickFixOneSignalSDK5Issues,
    testLoginMethods: async () => {
      const adapter = getOneSignalAdapter();
      return await testSDK5LoginMethods(adapter);
    }
  };
} 